import {
  KeyFeaturesSectionView,
  KeyFeaturesSectionConfig,
} from "@cma/presentation-generation-logic";
import { textFormatter } from "@shared/utils/text-formatter";
import { EditableKeyFeatures } from "./helpers/key-features-form-helpers";

type Props = {
  sectionView: KeyFeaturesSectionView;
  hideEmptyFeatures?: boolean;
};

type KeyFeatureOption = {
  configName: keyof KeyFeaturesSectionConfig;
  valueName?: EditableKeyFeatures;
  title: string;
  value: string;
  show?: boolean;
  isEmpty?: boolean;
};

export const useKeyFeaturesLogic = (props: Props) => {
  const { sectionView, hideEmptyFeatures = false } = props;
  const {
    propertyType,
    yearBuilt,
    neighborhood,
    livingSpace,
    lotSize,
    baths,
    beds,
    garageParkingSpaces,
    costPerInternalSqFt,
    listPrice,
    soldPrice,
    roomsTotal,
    // flipTaxRemarks,
    hoaFees,
    hoaFee,
    hasDoormanAttendance,
    preferences,
  } = sectionView;

  const keyFeaturesOptions: KeyFeatureOption[] = [
    {
      configName: "showPropertyType",
      valueName: "propertyType",
      title: "Property Type",
      value: textFormatter.valueToText(propertyType),
      show: preferences?.showPropertyType,
      isEmpty: !propertyType,
    },
    {
      configName: "showYearBuilt",
      valueName: "yearBuilt",
      title: "Year Built",
      value: textFormatter.valueToText(yearBuilt),
      show: preferences?.showYearBuilt,
      isEmpty: !yearBuilt,
    },
    {
      configName: "showLivingSpace",
      valueName: "livingSpace",
      title: "Living Area",
      value: textFormatter.area(livingSpace),
      show: preferences?.showLivingSpace,
      isEmpty: !livingSpace,
    },
    {
      configName: "showBeds",
      valueName: "beds",
      title: "Beds",
      value: textFormatter.valueToText(beds),
      show: preferences?.showBeds,
      isEmpty: !beds,
    },
    {
      configName: "showBaths",
      valueName: "baths",
      title: "Baths",
      value: textFormatter.valueToText(baths),
      show: preferences?.showBaths,
      isEmpty: !baths,
    },
    {
      configName: "showListPrice",
      valueName: "listPrice",
      title: "List Price",
      value: textFormatter.currency(listPrice),
      show: preferences?.showListPrice,
      isEmpty: !listPrice,
    },
    {
      configName: "showSoldPrice",
      valueName: "soldPrice",
      title: "Sold Price",
      value: textFormatter.currency(soldPrice),
      show: preferences?.showSoldPrice,
      isEmpty: !soldPrice,
    },
    {
      configName: "showNeighborhood",
      valueName: "neighborhood",
      title: "Neighborhood",
      value: textFormatter.valueToText(neighborhood),
      show: preferences?.showNeighborhood,
      isEmpty: !neighborhood,
    },
    {
      configName: "showRoomsTotal",
      valueName: "roomsTotal",
      title: "Total Rooms",
      value: textFormatter.valueToText(roomsTotal),
      show: preferences?.showRoomsTotal,
      isEmpty: !roomsTotal,
    },
    {
      configName: "showLotSize",
      valueName: "lotSize",
      title: "Lot Size",
      value: textFormatter.area(lotSize),
      show: preferences?.showLotSize,
      isEmpty: !lotSize,
    },
    {
      configName: "showGarageParkingSpaces",
      valueName: "garageParkingSpaces",
      title: "Garage Parking",
      value: textFormatter.valueToText(garageParkingSpaces),
      show: preferences?.showGarageParkingSpaces,
      isEmpty: !garageParkingSpaces,
    },
    {
      configName: "showDoormanAttendance",
      valueName: "hasDoormanAttendance",
      title: "Doorman",
      value: textFormatter.doormanAttendance(hasDoormanAttendance),
      show: preferences?.showDoormanAttendance,
      isEmpty:
        hasDoormanAttendance === null || hasDoormanAttendance === undefined,
    },
    {
      configName: "showPricePerLivingSpaceSqft",
      title: "Cost Per Sq Ft",
      value: textFormatter.currency(costPerInternalSqFt),
      show: preferences?.showPricePerLivingSpaceSqft,
      isEmpty: !costPerInternalSqFt,
    },
    // {
    //   configName: "showFlipTaxRemarks",
    //   valueName: "flipTaxRemarks",
    //   title: "Flip Tax",
    //   value: textFormatter.valueToText(flipTaxRemarks),
    //   show: preferences?.showFlipTaxRemarks,
    //   isEmpty: !flipTaxRemarks,
    // },
    {
      configName: "showHOAFees",
      valueName: "hoaFees",
      title: "Monthly HOA Fees",
      value: textFormatter.hoaFeeAndFrequency({
        legacyHoaFees: hoaFees,
        hoaFeeAndFrequency: hoaFee,
      }),
      show: preferences?.showHOAFees,
      isEmpty: !hoaFees,
    },
  ];

  const visibleKeyFeaturesOptions = keyFeaturesOptions.map((feature) => ({
    ...feature,
    show: feature.show && (!hideEmptyFeatures || !feature.isEmpty),
  }));

  return { keyFeaturesOptions: visibleKeyFeaturesOptions };
};
